import { Avatar, Box, AvatarProps } from '@mui/material';
import TierBadge, { SubscriptionTier, UserRole } from './TierBadge';

interface AvatarWithBadgeProps extends Omit<AvatarProps, 'children'> {
  name?: string;
  avatarUrl?: string;
  avatarColor?: string;
  tier?: SubscriptionTier;
  userRole?: UserRole;
  size?: number;
  badgeSize?: number;
  showBadge?: boolean;
  onAvatarClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onBadgeClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

function getInitials(name?: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function AvatarWithBadge({
  name,
  avatarUrl,
  avatarColor,
  tier = 'free',
  userRole,
  size = 40,
  badgeSize,
  showBadge = true,
  onAvatarClick,
  onBadgeClick,
  sx,
  ...avatarProps
}: AvatarWithBadgeProps) {
  // Calculate badge size based on avatar size
  const calculatedBadgeSize = badgeSize || Math.max(16, size * 0.4);

  // Badge position offset (5 o'clock position)
  const badgeOffset = size * 0.15;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
      }}
    >
      <Avatar
        src={avatarUrl}
        onClick={onAvatarClick}
        sx={{
          width: size,
          height: size,
          bgcolor: avatarColor || '#5D5FEF',
          fontSize: size * 0.4,
          fontWeight: 600,
          cursor: onAvatarClick ? 'pointer' : 'default',
          ...sx,
        }}
        {...avatarProps}
      >
        {!avatarUrl && getInitials(name)}
      </Avatar>

      {showBadge && (tier || userRole === 'super_admin') && (
        <Box
          onClick={(e) => {
            if (onBadgeClick) {
              e.stopPropagation();
              onBadgeClick(e);
            }
          }}
          sx={{
            position: 'absolute',
            bottom: -badgeOffset / 2,
            right: userRole === 'super_admin' ? -badgeOffset : -badgeOffset / 2,
            backgroundColor: 'white',
            borderRadius: userRole === 'super_admin' ? '8px' : '50%',
            padding: '2px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: onBadgeClick ? 'pointer' : 'default',
            '&:hover': onBadgeClick ? {
              transform: 'scale(1.1)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            } : {},
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
        >
          <TierBadge tier={tier} userRole={userRole} size={calculatedBadgeSize} />
        </Box>
      )}
    </Box>
  );
}
